import { omit } from 'lodash';

/**
 * The policies `useFinalizeSession` saves for these selected quotes, each on the client it is saved
 * to: a CCRC couple selection saves one membership on each partner.
 */
export function listSelectedQuotePolicies(
  selectedQuotes: Record<string, SelectedQuoteData> | undefined,
  clientId: string,
  partnerClientId: string | undefined,
): FinalizedPolicyRef[] {
  return Object.values(selectedQuotes ?? {}).flatMap(({ ccrcCouplePolicies, policyFundingSource }) => {
    if (ccrcCouplePolicies && partnerClientId) {
      return [
        { clientId, fundingSourceId: ccrcCouplePolicies.primary.pendingFundingSourceId },
        {
          clientId: partnerClientId,
          fundingSourceId: ccrcCouplePolicies.partner.pendingFundingSourceId,
        },
      ];
    }
    return [{ clientId, fundingSourceId: policyFundingSource.pendingFundingSourceId }];
  });
}

export function isSamePolicy(a: FinalizedPolicyRef, b: FinalizedPolicyRef): boolean {
  return a.clientId === b.clientId && a.fundingSourceId === b.fundingSourceId;
}

/**
 * The balance updates that give back what finalizing deducted. Finalizing took the first selected
 * quote's allocation from each funded account, flooring the balance at 0, so the amount taken was
 * `min(balance then, allocation)`; the balance then is the session's `multipleFundingSourcesSnapshot`.
 * It is added back to the current balance, so edits made since finalizing are kept.
 */
export function buildDeductionRestorations(
  stepData: GuidedPlanningStepData | undefined,
  currentSelfFunding: MultipleFundingSources['selfFunding'] | undefined,
): Array<{ fundingSourceId: string; existingAssetsValue: number }> {
  const [selectedQuoteId] = Object.keys(stepData?.selectedQuotes ?? {});
  if (!selectedQuoteId) return [];

  const allocations = stepData?.policyFundingAllocations?.[selectedQuoteId] ?? {};
  const balancesThen = stepData?.multipleFundingSourcesSnapshot?.selfFunding ?? {};

  return Object.entries(allocations).flatMap(([fundingSourceId, allocated]) => {
    const source = currentSelfFunding?.[fundingSourceId];
    if (!source || !allocated || allocated <= 0) return [];

    const balanceThen = balancesThen[fundingSourceId]?.existingAssetsValue ?? allocated;
    const deducted = Math.min(Math.max(balanceThen, 0), allocated);
    if (deducted <= 0) return [];

    return [{ fundingSourceId, existingAssetsValue: (source.existingAssetsValue ?? 0) + deducted }];
  });
}

/**
 * `fundingSources` without the policies an earlier finalization saved on `clientId`, so a reopened
 * plan doesn't count its own policy as existing coverage. The same object when there are none.
 */
export function omitPreviouslyFinalizedPolicies(
  fundingSources: MultipleFundingSources | undefined,
  clientId: string | undefined,
  previouslyFinalizedPolicies: readonly FinalizedPolicyRef[] | undefined,
): MultipleFundingSources | undefined {
  const policies = fundingSources?.ltcPolicy;
  const ids = (previouslyFinalizedPolicies ?? [])
    .filter(ref => ref.clientId === clientId)
    .map(ref => ref.fundingSourceId)
    .filter(id => policies && id in policies);
  if (!fundingSources || ids.length === 0) return fundingSources;

  return { ...fundingSources, ltcPolicy: omit(fundingSources.ltcPolicy, ids) };
}

/** `client` with `omitPreviouslyFinalizedPolicies` applied; the same object when nothing is removed. */
export function withoutPreviouslyFinalizedPolicies<T extends Client | null>(
  client: T,
  previouslyFinalizedPolicies: readonly FinalizedPolicyRef[] | undefined,
): T {
  if (!client) return client;
  const fundingSources = omitPreviouslyFinalizedPolicies(
    client.multipleFundingSources,
    client.clientId,
    previouslyFinalizedPolicies,
  );
  if (fundingSources === client.multipleFundingSources || !fundingSources) return client;
  return { ...client, multipleFundingSources: fundingSources };
}
